<template>
  <section>
    <em-header :headline="material.name">
      <em-saving-state :saving="saving" />
      <em-action-button
        :disabled="actionButtonDisabled"
        :action="onFinishClicked"
      >
        {{ buttonLabel }}
      </em-action-button>
    </em-header>

    <div class="iframe-container view" />
  </section>
</template>

<style lang="stylus">
  .iframe-container
    padding-top calc(2 * var(--gap))
    iframe
      display: block
      height 100%
      width 100%
      border none
</style>

<script>
  import EmHeader from '../../components/EmHeader'
  import { mapActions } from 'vuex'
  import EmActionButton from '../../components/EmActionButton'
  import EmSavingState from '../../components/EmSavingState'
  import hasDebounce from '../../mixins/hasDebounce'
  import hasIframe from '../../mixins/hasIframe'
  import ContentAppStartup from '../../models/ContentAppStartup'
  import { getParams } from '../../lib/utils'

  export default {
    name: 'MaterialEdit',

    components: { EmSavingState, EmActionButton, EmHeader },

    mixins: [
      hasDebounce,
      hasIframe
    ],

    props: {
      material: {
        type: Object,
        required: true
      },

      buttonLabel: {
        type: String,
        default: ''
      },

      isCopy: {
        type: Boolean,
        default: false
      }
    },

    data () {
      return {
        savingEnabled: true,
        saving: false,
        finalizing: false,
        contentAppStartupData: null
      }
    },

    computed: {
      actionButtonDisabled () {
        return !this.savingEnabled || this.saving || this.finalizing
      }
    },

    mounted () {
      // disable the root loading state (the fullscreen loading)
      this.init()
      window.addEventListener('message', this.onMessageHandler)
    },

    beforeDestroy () {
      window.removeEventListener('message', this.onMessageHandler)
    },

    methods: {
      ...mapActions('ui', [
        'setLoading'
      ]),

      ...mapActions('steps', [
        'execute',
        'mergeToState'
      ]),

      onFinishClicked () {
        this.disableBriefing()

        return this.initiateSave(true)
      },

      /**
      * Is executed on mounting this component
      *
      * @return {Promise<void>}
      */
      async init () {
        // create an iframe which will host the content app (aka briefing)
        this.buildIframe()

        // fetch the startup content from the api which will tell us which url
        // we have to open
        this.contentAppStartupData = await ContentAppStartup.fetch(this.material.id)

        // add isCopy to url
        let url
        if (this.contentAppStartupData.url.includes('?')) {
          url = this.contentAppStartupData.url + '&isCopy=' + (this.isCopy ? 1 : 0)
        } else {
          url = this.contentAppStartupData.url + '?isCopy=' + (this.isCopy ? 1 : 0)
        }
        // so lets open the content app
        this.browseTo(url)

        // if the app is hosted somewhere else (not on scp api) we have to post the initilization data to
        // the content app via post message – this is done here
        this.$once('iframe-loaded', () => {
          this.sendMessages()
          this.$loading(false)
          if (this.contentAppStartupData.isHostedExternally) {
            this.contentAppStartupData.sendInitialization(this.iframe)
          }
        })
      },

      initiateSave (finalize = false) {
        this.saving = true
        this.finalizing = finalize

        const p = new Promise((resolve) => {
          this.resolver = resolve

          // triggers saveContent
          this.iframe.contentWindow.postMessage({ command: 'sc2.briefing.getContent' }, '*')
        })

        return p.then(() => {
          this.saving = false
        })
      },

      saveContent (content) {
        // set the content to the state
        this.mergeToState({ content, _done: this.finalizing })

        // save it
        const saving = this.execute()

        return !this.resolver
          ? saving
          : saving.then(this.resolver)
      },

      sendMessages () {
        // set vault session id
        this.iframe.contentWindow.postMessage({
          command: 'sc2.briefing.setVaultSessionId',
          content: {
            sessionId: getParams('vault_session_id')
          }
        }, '*')

        // enable the briefing
        this.iframe.contentWindow.postMessage({ command: 'sc2.briefing.enable' }, '*')
      },

      disableBriefing () {
        this.iframe.contentWindow.postMessage({ command: 'sc2.briefing.disable' }, '*')
      },

      initializeContentAppSDK () {
        const initObject = this.contentAppStartupData.initObject
        const payload = {
          apiUrl: initObject.backendUrl + '/api/v1',
          token: initObject.token,
          material: initObject.material.id,
          isNewMaterial: initObject.content.length
        }

        this.iframe.contentWindow.postMessage({
          command: 'scp.content-app-sdk.initialized',
          payload: payload
        }, '*')
      },

      onMessageHandler (e) {
        if (e.data.command === 'sc2.briefing.mark-dirty') {
          this.debounces(this.initiateSave)
        }

        if (e.data.command === 'sc2.briefing.updateContent') {
          this.saveContent(e.data.content)
        }

        if (e.data.command === 'sc2.briefing.setFieldValue') {
          /* if (this.material.getFieldValue(e.data.selector) !== e.data.value) {
            this.material.setFieldValue(e.data.selector, e.data.value)
            this.material.put()
          } */
        }

        if (e.data.command === 'sc2.briefing.setLoadingState') {
          this.$loading(e.data.isLoading)
        }

        if (e.data.command === 'scp.iframe_view.saving') {
          this.savingEnabled = e.data.enabled
        }

        if (e.data.command === 'scp.content-app-sdk.initialize') {
          this.initializeContentAppSDK()
        }
      }
    }
  }
</script>
